/* PanelPartners mega rail: vertical tablist of child collections; hovering,
   clicking or keyboard-focusing a tab swaps the right-hand product cards.
   ARIA tablist/tab/tabpanel with roving tabindex + arrow-key nav. Open/close of
   the whole panel is pp-nav's job; Escape bubbles up to pp-nav which closes it.
   Custom element so it re-initialises on theme-editor section reload. */
if (!customElements.get('pp-mega-rail')) {
  class PPMegaRail extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.onKeydown = this.onKeydown.bind(this);
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab));
        tab.addEventListener('mouseenter', () => this.activate(tab));
        tab.addEventListener('keydown', this.onKeydown);
      });
    }

    activate(tab) {
      if (tab.getAttribute('aria-selected') === 'true') return;
      this.tabs.forEach((other) => {
        const selected = other === tab;
        other.setAttribute('aria-selected', selected);
        other.setAttribute('tabindex', selected ? '0' : '-1');
        const panel = document.getElementById(other.getAttribute('aria-controls'));
        if (panel) panel.toggleAttribute('hidden', !selected);
      });
    }

    onKeydown(event) {
      const index = this.tabs.indexOf(event.target);
      if (index === -1) return;
      let next;
      switch (event.key) {
        case 'ArrowUp':
        case 'ArrowLeft':
          next = index === 0 ? this.tabs.length - 1 : index - 1;
          break;
        case 'ArrowDown':
        case 'ArrowRight':
          next = index === this.tabs.length - 1 ? 0 : index + 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = this.tabs.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      this.tabs[next].focus();
      this.activate(this.tabs[next]);
    }
  }

  customElements.define('pp-mega-rail', PPMegaRail);
}

/* PanelPartners header nav: N independent dropdown/mega disclosures.
   - Desktop: hover opens, click toggles.
   - Keyboard: Enter/Space toggles, Escape closes and returns focus.
   - Only one panel open at a time.
   Custom element so it re-initialises on theme-editor section reload. */
if (!customElements.get('pp-nav')) {
  class PPNav extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.pp-nav__item[data-has-panel]'));
      this.openItem = null;
      this.hoverCapable = window.matchMedia('(hover: hover) and (min-width: 990px)');

      this.items.forEach((item) => {
        const trigger = item.querySelector('.pp-nav__link');
        const panel = item.querySelector('.pp-nav__panel');
        if (!trigger || !panel) return;

        trigger.addEventListener('click', (event) => {
          // A link trigger (category bar) should navigate on click; the mega
          // still opens on hover. Only hijack the click for button triggers.
          if (trigger.tagName === 'A' && trigger.getAttribute('href')) return;
          event.preventDefault();
          this.toggle(item);
        });

        item.addEventListener('mouseenter', () => {
          if (this.hoverCapable.matches) this.open(item);
        });
        item.addEventListener('mouseleave', () => {
          if (this.hoverCapable.matches) this.close(item);
        });
      });

      this.onKeydown = this.onKeydown.bind(this);
      this.onDocClick = this.onDocClick.bind(this);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
    }

    toggle(item) {
      if (this.openItem === item) this.close(item);
      else this.open(item);
    }

    open(item) {
      if (this.openItem && this.openItem !== item) this.close(this.openItem);
      const trigger = item.querySelector('.pp-nav__link');
      const panel = item.querySelector('.pp-nav__panel');
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      this.openItem = item;
    }

    close(item) {
      const trigger = item.querySelector('.pp-nav__link');
      const panel = item.querySelector('.pp-nav__panel');
      trigger.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      if (this.openItem === item) this.openItem = null;
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.openItem) {
        const trigger = this.openItem.querySelector('.pp-nav__link');
        this.close(this.openItem);
        if (trigger) trigger.focus();
      }
    }

    onDocClick(event) {
      if (this.openItem && !this.contains(event.target)) this.close(this.openItem);
    }
  }

  customElements.define('pp-nav', PPNav);
}

/* PanelPartners mobile slide-out drawer.
   - Opens from a .pp-hamburger button (aria-controls -> this element's id).
   - Level 1 = categories; drilling a category slides its level-2 panel over.
   - Overlay click / Escape close; body scroll locked while open.
   - Focus moves into the visible level and is trapped; returns to the
     trigger that opened it (hamburger on close, category button on back).
   Custom element so it re-initialises on theme-editor section reload. */
if (!customElements.get('pp-drawer')) {
  const FOCUSABLE =
    'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';

  class PPDrawer extends HTMLElement {
    connectedCallback() {
      // Portal to <body> so the drawer isn't trapped in the header's stacking
      // context (otherwise the announcement bar / sticky header can paint over
      // it). Guard against the theme editor re-rendering a duplicate.
      if (this.parentElement !== document.body) {
        document.querySelectorAll('#PPDrawer').forEach((el) => {
          if (el !== this) el.remove();
        });
        document.body.appendChild(this);
        return; // appendChild re-runs connectedCallback with body as parent
      }

      this.panel = this.querySelector('.pp-drawer__panel');
      this.level1 = this.querySelector('.pp-drawer__level--1');
      this.hamburger = document.querySelector('.pp-hamburger');
      this.activePanel = null; // open level-2 element
      this.drillTrigger = null; // category button that opened it

      this.onKeydown = this.onKeydown.bind(this);
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.onBreakpoint = () => {
        if (this.desktop.matches) this.close();
      };
      this.desktop.addEventListener('change', this.onBreakpoint);
      this.onResize = () => this.positionBelowHeader();

      if (this.hamburger) {
        this.hamburger.addEventListener('click', () => {
          if (this.isOpen) this.close();
          else this.open();
        });
      }

      this.addEventListener('click', (event) => {
        const drill = event.target.closest('[data-drill]');
        if (drill && this.contains(drill)) {
          this.openCategory(drill.getAttribute('data-drill'), drill);
          return;
        }
        if (event.target.closest('[data-pp-back]')) {
          this.back();
          return;
        }
        if (event.target.closest('[data-pp-close]')) {
          this.close();
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      this.desktop.removeEventListener('change', this.onBreakpoint);
      document.body.classList.remove('pp-drawer-open');
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    // Offset the drawer to start at the header's bottom edge so the menu
    // unfolds below the header instead of covering it.
    positionBelowHeader() {
      const header = document.querySelector('.pp-header');
      const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      this.style.setProperty('--pp-drawer-top', top + 'px');
    }

    open() {
      if (this.isOpen) return;
      this.positionBelowHeader();
      this.hidden = false;
      // next frame so the transition runs from the hidden state
      requestAnimationFrame(() => this.classList.add('is-open'));
      document.body.classList.add('pp-drawer-open');
      if (this.hamburger) this.hamburger.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize);
      this.focusFirst(this.level1);
    }

    close() {
      if (!this.isOpen && this.hidden) return;
      this.classList.remove('is-open');
      document.body.classList.remove('pp-drawer-open');
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
      if (this.hamburger) {
        this.hamburger.setAttribute('aria-expanded', 'false');
        this.hamburger.focus();
      }
      // reset to level 1 after the slide-out finishes
      const reset = () => {
        this.hidden = true;
        this.resetLevels();
      };
      const onEnd = (e) => {
        if (e.target !== this.panel) return;
        this.panel.removeEventListener('transitionend', onEnd);
        reset();
      };
      this.panel.addEventListener('transitionend', onEnd);
      // fallback if no transition fires
      setTimeout(() => {
        this.panel.removeEventListener('transitionend', onEnd);
        if (!this.isOpen) reset();
      }, 400);
    }

    openCategory(index, trigger) {
      const panel = this.querySelector('.pp-drawer__level--2[data-cat-panel="' + index + '"]');
      if (!panel) return;
      if (this.activePanel) this.activePanel.hidden = true;
      panel.hidden = false;
      panel.classList.add('is-active');
      this.activePanel = panel;
      this.drillTrigger = trigger;
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      this.focusFirst(panel);
    }

    back() {
      if (!this.activePanel) return;
      this.activePanel.classList.remove('is-active');
      const panel = this.activePanel;
      const trigger = this.drillTrigger;
      const onEnd = (e) => {
        if (e.target !== panel) return;
        panel.removeEventListener('transitionend', onEnd);
        panel.hidden = true;
      };
      panel.addEventListener('transitionend', onEnd);
      setTimeout(() => {
        panel.removeEventListener('transitionend', onEnd);
        if (!panel.classList.contains('is-active')) panel.hidden = true;
      }, 400);
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
      }
      this.activePanel = null;
      this.drillTrigger = null;
    }

    resetLevels() {
      this.querySelectorAll('.pp-drawer__level--2').forEach((p) => {
        p.classList.remove('is-active');
        p.hidden = true;
      });
      this.querySelectorAll('[data-drill]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      this.activePanel = null;
      this.drillTrigger = null;
    }

    currentLevel() {
      return this.activePanel || this.level1;
    }

    focusFirst(level) {
      const target = level && level.querySelector(FOCUSABLE);
      if (target) target.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      // trap focus within the currently visible level
      const level = this.currentLevel();
      const items = Array.from(level.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('pp-drawer', PPDrawer);
}
